import React from 'react';
import styled from 'styled-components';

import {Link} from '../components/globalStyles';
import {AppRoute, AppRoutePathFunction} from '../logic/routes';
import BitmovinLogo from '../media/hexagonIconImage/icons/about_us.svg';
import ExceptionalViewerExperienceSvg from '../media/hexagonIconImage/icons/exceptional_viewer_experience.svg';
import OperationalOptimizationSvg from '../media/hexagonIconImage/icons/operational_optimization.svg';

const StyledLink = styled(Link)`
  color: rgb(31, 169, 224);
  :hover {
    text-decoration: underline;
  }
`;

export interface ILiveEncodingNoteworthyFeature {
  readonly name: string;
  readonly description: JSX.Element;
  readonly iconPath: string;
}

const perTitleForLive = AppRoutePathFunction.DemoDetail({demoId: '357d1337-bc91-11ec-a62d-42010a8400cb'});
const perTitleVod = AppRoutePathFunction.DemoDetail({demoId: 'b57de324-980a-11e9-b128-42010a840215'});
const liveStreamingSRT = AppRoutePathFunction.DemoDetail({demoId: 'b5953808-980a-11e9-b128-42010a840215'});
const liveStreamingZixi = AppRoutePathFunction.DemoDetail({demoId: 'b5953722-980a-11e9-b128-42010a840215'});
const hollywoodDRM = AppRoutePathFunction.DemoDetail({demoId: 'b5895159-980a-11e9-b128-42010a840215'});
const aws = AppRoutePathFunction.DemoDetail({demoId: 'b595385e-980a-11e9-b128-42010a840215'});
const gcp = AppRoutePathFunction.DemoDetail({demoId: 'b595365b-980a-11e9-b128-42010a840215'});
const live2Vod = AppRoutePathFunction.DemoDetail({demoId: 'b5af0a4e-980a-11e9-b128-42010a840215'});

const nextGenVod = AppRoutePathFunction.Encoding();

const LiveEncodingNoteworthyFeatureData: ReadonlyArray<ILiveEncodingNoteworthyFeature> = [
  {
    name: 'High quality',
    description: (
      <>
        Supporting <StyledLink to={liveStreamingSRT}>SRT</StyledLink> and{' '}
        <StyledLink to={liveStreamingZixi}>Zixi</StyledLink> protocols with redundant inputs and packet protection, we
        can ingest live content at high bitrates, ensuring a high quality, protected input. The Live Encoder is built
        from the same core encoding technology that powers our market leading{' '}
        <StyledLink to={nextGenVod}>Next Generation VOD Encoder</StyledLink>, delivering measurably high quality video
        and audio to multiple platforms. Protect this content by integrating{' '}
        <StyledLink to={hollywoodDRM}>Hollywood grade DRM</StyledLink>.
      </>
    ), // tslint:disable-line:max-line-length
    iconPath: ExceptionalViewerExperienceSvg
  },
  {
    name: 'Ease of use',
    description: (
      <>
        Offering a managed cloud service or running in a customer owned connected{' '}
        <StyledLink to={aws}>AWS</StyledLink> or <StyledLink to={gcp}>Google</StyledLink> cloud accounts.
        Our comprehensive APIs make it easy to orchestrate and deploy complex workflows, alternatively customers can
        get streaming quickly with our Simple Encoding API for Live or use our Dashboard Live Encoding wizards. Get
        your VoD assets online immediately and seamlessly after the
        live event with our <StyledLink to={live2Vod}>Live to VOD</StyledLink>.
      </>
    ), // tslint:disable-line:max-line-length
    iconPath: OperationalOptimizationSvg
  },
  {
    name: 'Always innovative',
    description: (
      <>
        Having led the industry with our <StyledLink to={perTitleVod}>Per-Title Encoding</StyledLink> for VOD, once
        again Bitmovin is at the forefront of development. The bleeding edge{' '}
        <StyledLink to={perTitleForLive}>Per-Title for Live</StyledLink> workflow assesses the complexity of the content
        in real-time to create the optimal bitrate ladder to maximize the quality of experience (QoE). Per-Title for
        Live is developed in collaboration with the ATHENA Christian Doppler (CD) Laboratory. This revolution has
        potential to lower the data requirements for Live content distribution whilst maintaining a high QoE for users.
      </>
    ), // tslint:disable-line:max-line-length
    iconPath: BitmovinLogo
  }
];

export default LiveEncodingNoteworthyFeatureData;
